/**
 * @fileoverview AiDisclaimer - the platform-wide AI output disclaimer
 *
 * Purpose: Render the canonical AI disclaimer at the foot of any section that
 * presents AI-generated output. The wording comes from `AI_DISCLAIMER` and
 * must never be retyped, paraphrased or truncated at a call site.
 *
 * One presentation only, by design: a quiet muted line. It must be visible
 * but subtle, and it appears once per section rather than on every nested
 * panel, so the app is not saturated with legal copy. A louder variant on
 * some surfaces would make the quiet one read as less binding.
 *
 * Deliberately not dismissible and never hidden behind a tooltip.
 *
 * @component
 * @example
 * <AiDisclaimer />
 * <AiDisclaimer className="mt-4" />
 */

import { AI_DISCLAIMER } from "@cvx/lib/aiDisclaimer";
import { cn } from "@/utils/misc";

interface AiDisclaimerProps {
  /**
   * Spacing/alignment overrides for the host surface. Not for restyling,
   * shrinking or hiding the text.
   */
  className?: string;
}

export function AiDisclaimer({ className }: AiDisclaimerProps) {
  return (
    <p
      className={cn(
        "text-[10px] leading-snug text-muted-foreground/60",
        className,
      )}
    >
      {AI_DISCLAIMER}
    </p>
  );
}
